import type { SluggedId } from "Models";
import { competitionsApi } from "../competitionsApi";
import { stripUndefinedParams } from "../queryParams";
import { parseEnvelopeListWithMeta } from "../responseEnvelope";
import { EventDtoSchema } from "entities/responses/competitions";
import type { EventDto } from "entities/responses/competitions";
import type { z } from "zod";
import {
  CreateEventDtoSchema,
  UpdateEventDtoSchema,
} from "entities/requests/competitions";

// Callers pass the *raw* draft (dates as Date objects, etc.); the endpoint's
// `.parse` below is the single source of validation + wire normalization.
// Using the schema output type here caused a double-parse: pre-parsing in the
// component emits an offset ISO string that `.parse` then rejects.
type CreateEventInput = z.input<typeof CreateEventDtoSchema>;
type UpdateEventInput = z.input<typeof UpdateEventDtoSchema>;

const urls = {
  facilityEvents(facilityId: string): string {
    return `/api/competitions/facility/${facilityId}/events`;
  },
  createEvent(facilityId: string, tourId: string): string {
    return `/api/competitions/facility/${facilityId}/tour/${tourId}/events`;
  },
  updateEvent(facilityId: string, tourId: string, eventId: string): string {
    return `/api/competitions/facility/${facilityId}/tour/${tourId}/event/${eventId}`;
  },
  // Same path as the PATCH; the PUT is the image upload.
  uploadEventImage(
    facilityId: string,
    tourId: string,
    eventId: string,
  ): string {
    return `/api/competitions/facility/${facilityId}/tour/${tourId}/event/${eventId}`;
  },
};

export interface GetFacilityEventsArgs {
  facilityId: string;
  tourIds?: SluggedId[];
  includePublic?: boolean;
}

/**
 * Valid events plus how many the API returned that we couldn't parse — so the
 * dashboard can surface "N couldn't be displayed" rather than silently showing
 * a short list.
 */
export interface FacilityEventsResult {
  events: EventDto[];
  invalidCount: number;
}

export interface CreateEventArgs {
  facilityId: string;
  tourId: string;
  body: CreateEventInput;
}

export interface UpdateEventArgs {
  facilityId: string;
  tourId: string;
  eventId: string;
  body: UpdateEventInput;
}

export interface UploadEventImageArgs {
  facilityId: string;
  tourId: string;
  eventId: string;
  /** The finished 360×180 JPG — the endpoint neither resizes nor validates. */
  file: Blob;
  /** Max 100 characters, enforced by the caller. */
  imageName: string;
}

const eventEndpoints = competitionsApi.injectEndpoints({
  endpoints: (build) => ({
    getFacilityEvents: build.query<FacilityEventsResult, GetFacilityEventsArgs>(
      {
        query: ({ facilityId, tourIds, includePublic }) => ({
          url: urls.facilityEvents(facilityId),
          method: "GET",
          params: stripUndefinedParams({ tourIds, includePublic }),
        }),
        // Individual per-item validation — real facility data can contain
        // events with an out-of-range numberOfRounds or a missing courseId;
        // one bad event must not blank out the whole list. The dropped count
        // rides alongside so the UI can flag degraded data.
        transformResponse: (raw: unknown) => {
          const { items, invalidCount } = parseEnvelopeListWithMeta(
            raw,
            EventDtoSchema,
          );
          return { events: items, invalidCount };
        },
        providesTags: (result) =>
          result
            ? [
                ...result.events.map((event) => ({
                  type: "Event" as const,
                  id: event.id,
                })),
                "Event",
              ]
            : ["Event"],
      },
    ),

    createEvent: build.mutation<EventDto, CreateEventArgs>({
      query: ({ facilityId, tourId, body }) => ({
        url: urls.createEvent(facilityId, tourId),
        method: "POST",
        body: CreateEventDtoSchema.parse(body),
      }),
      transformResponse: (raw: unknown) => EventDtoSchema.parse(raw),
      // A new event changes its tour's eventCount, so both entities invalidate.
      invalidatesTags: ["Event", "Tour"],
    }),

    /**
     * The existing image endpoint, unchanged. It stores whatever file it is
     * given, so the browser has already cropped and resized to the
     * simulator's 360×180.
     */
    uploadEventImage: build.mutation<EventDto, UploadEventImageArgs>({
      query: ({ facilityId, tourId, eventId, file, imageName }) => {
        const form = new FormData();
        form.append("File", file, imageName);
        form.append("ImageName", imageName);
        // Warnings are surfaced to the operator rather than silently accepted.
        form.append("OverrideWarnings", "false");
        return {
          url: urls.uploadEventImage(facilityId, tourId, eventId),
          method: "PUT",
          body: form,
        };
      },
      transformResponse: (raw: unknown) => EventDtoSchema.parse(raw),
      invalidatesTags: (_result, _error, { eventId }) => [
        { type: "Event", id: eventId },
      ],
    }),

    updateEvent: build.mutation<EventDto, UpdateEventArgs>({
      query: ({ facilityId, tourId, eventId, body }) => ({
        url: urls.updateEvent(facilityId, tourId, eventId),
        method: "PATCH",
        body: UpdateEventDtoSchema.parse(body),
      }),
      transformResponse: (raw: unknown) => EventDtoSchema.parse(raw),
      invalidatesTags: (_result, _error, { eventId }) => [
        { type: "Event", id: eventId },
        "Tour",
      ],
    }),
  }),
});

export const {
  useGetFacilityEventsQuery,
  useCreateEventMutation,
  useUpdateEventMutation,
  useUploadEventImageMutation,
} = eventEndpoints;
